import { CadPolygon } from '../new-project-cad/types';
import { DXFData } from '../new-project/dxf/types';

/**
 * A top-down picture of a plan (polygons in their colours, holes cut out) as base64 PNG without the
 * data: prefix: the preview of a drawing and of a project created from it. Plain 2D canvas. With
 * `lines` (the imported CAD drawing) they are drawn faintly underneath, and a drawing that has no
 * polygons yet still gets a picture.
 */
export function renderPlanThumbnail(
  polygons: Pick<CadPolygon, 'points' | 'holes' | 'color'>[],
  size = 640,
  lines: DXFData | null = null,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = Math.round(size * 0.625);
  // Transparent: the picture sits on the card behind it.
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  let [minX, maxX, minY, maxY] = [Infinity, -Infinity, Infinity, -Infinity];
  const extend = ([x, y]: [number, number]) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  };
  polygons.forEach((p) => p.points.forEach(extend));
  lines?.entities.forEach((e) => e.vertices.forEach((v) => extend([v[0], v[1]])));
  if (minX <= maxX) {
    const pad = 0.06;
    const scale = Math.min(
      (canvas.width * (1 - 2 * pad)) / Math.max(maxX - minX, 1e-9),
      (canvas.height * (1 - 2 * pad)) / Math.max(maxY - minY, 1e-9),
    );
    const offsetX = (canvas.width - (maxX - minX) * scale) / 2;
    const offsetY = (canvas.height - (maxY - minY) * scale) / 2;
    // Canvas y grows downwards; the plan's grows upwards.
    const at = ([x, y]: [number, number]): [number, number] => [
      offsetX + (x - minX) * scale,
      canvas.height - offsetY - (y - minY) * scale,
    ];
    if (lines) {
      ctx.beginPath();
      for (const entity of lines.entities) {
        const v = entity.vertices;
        if (v.length < 2) continue;
        v.forEach((p, i) => (i === 0 ? ctx.moveTo(...at([p[0], p[1]])) : ctx.lineTo(...at([p[0], p[1]]))));
        if (entity.closed) ctx.closePath();
      }
      ctx.strokeStyle = 'rgba(140, 170, 200, 0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    const ring = (points: [number, number][]) => {
      points.forEach((p, i) => (i === 0 ? ctx.moveTo(...at(p)) : ctx.lineTo(...at(p))));
      ctx.closePath();
    };
    for (const polygon of polygons) {
      if (polygon.points.length < 3) continue;
      ctx.beginPath();
      ring(polygon.points);
      polygon.holes?.forEach(ring);
      ctx.fillStyle = polygon.color;
      ctx.fill('evenodd');
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }
  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}
